import React, { useState, useEffect } from 'react';

interface ProfilePageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
}

interface ClientProfile {
  id: string;
  email: string;
  full_name: string;
  gender: 'male' | 'female' | '';
  age: number | '';
  height_cm: number | '';
  weight_kg: number | '';
  activity_level: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active' | 'extra_active' | '';
  primary_goal: string;
  dietary_restrictions: string;
  allergies: string;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ user, coachMode, clientId }) => {
  const [activeTab, setActiveTab] = useState<string>('account');
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Load client profile data
  useEffect(() => {
    if (coachMode && clientId) {
      loadClientProfile();
    } else if (user) {
      // Load current user's profile
      setProfile({
        id: user.id,
        email: user.email || '',
        full_name: user.full_name || user.fullName || '',
        gender: user.gender || '',
        age: user.age || '',
        height_cm: user.height_cm || '',
        weight_kg: user.weight_kg || '',
        activity_level: user.activity_level || '',
        primary_goal: user.primary_goal || '',
        dietary_restrictions: user.dietary_restrictions || '',
        allergies: user.allergies || '',
      });
    }
  }, [coachMode, clientId, user]);

  const loadClientProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/coach/clients/${clientId}/profile`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        }
      );

      const data = await response.json();

      if (data.success && data.profile) {
        setProfile({
          id: data.profile.id,
          email: data.profile.email || '',
          full_name: data.profile.full_name || '',
          gender: data.profile.gender || '',
          age: data.profile.age || '',
          height_cm: data.profile.height_cm || '',
          weight_kg: data.profile.weight_kg || '',
          activity_level: data.profile.activity_level || '',
          primary_goal: data.profile.primary_goal || '',
          dietary_restrictions: data.profile.dietary_restrictions || '',
          allergies: data.profile.allergies || '',
        });

        console.log('✅ Profile loaded with recommendations:', data.profile);
      }
    } catch (error) {
      console.error('Error loading client profile:', error);
      setMessage({ type: 'error', text: 'Failed to load client profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    setSaving(true);
    setMessage(null);

    try {
      const url = coachMode && clientId
        ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/coach/clients/${clientId}/profile`
        : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/profile`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          full_name: profile.full_name,
          gender: profile.gender,
          age: profile.age,
          height_cm: profile.height_cm,
          weight_kg: profile.weight_kg,
          activity_level: profile.activity_level,
          primary_goal: profile.primary_goal,
          dietary_restrictions: profile.dietary_restrictions,
          allergies: profile.allergies,
        })
      });

      const data = await response.json();

      if (data.success || response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        if (coachMode && clientId) {
          loadClientProfile(); // Reload to confirm changes
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof ClientProfile, value: any) => {
    setProfile(prev => prev ? { ...prev, [field]: value } : null);
  };

  const tabs = [
    { id: 'account', label: 'Account Details', icon: '👤' },
    { id: 'preferences', label: 'Preferences', icon: '🎛️' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No profile data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <h1 className="text-2xl font-bold text-white">
            {coachMode ? `Client Profile: ${profile.full_name}` : 'Profile & Settings'} 👤
          </h1>
          <p className="mt-2 text-indigo-100">
            {coachMode
              ? 'View and manage client account settings and preferences'
              : 'Account settings and personal information'
            }
          </p>
        </div>
      </div>

      {/* Success/Error Message */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'account' && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
            <p className="text-sm text-gray-500 mt-1">
              {coachMode ? 'Update client profile information' : 'Update your personal information and health metrics'}
            </p>
          </div>
          <div className="card-body">
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={profile.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
              </div>

              {/* Health Metrics */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4 mt-6">Health Metrics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender
                    </label>
                    <select
                      value={profile.gender}
                      onChange={(e) => handleChange('gender', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Age
                    </label>
                    <input
                      type="number"
                      value={profile.age}
                      onChange={(e) => handleChange('age', e.target.value ? parseInt(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                      placeholder="Age"
                      min="1"
                      max="120"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      value={profile.height_cm}
                      onChange={(e) => handleChange('height_cm', e.target.value ? parseInt(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                      placeholder="Height in cm"
                      min="50"
                      max="300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={profile.weight_kg}
                      onChange={(e) => handleChange('weight_kg', e.target.value ? parseFloat(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                      placeholder="Weight in kg"
                      min="20"
                      max="300"
                    />
                  </div>
                </div>
              </div>

              {/* Activity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity Level
                </label>
                <select
                  value={profile.activity_level}
                  onChange={(e) => handleChange('activity_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                >
                  <option value="">Select activity level</option>
                  <option value="sedentary">Sedentary (Little or no exercise)</option>
                  <option value="lightly_active">Lightly Active (Exercise 1-3 days/week)</option>
                  <option value="moderately_active">Moderately Active (Exercise 3-5 days/week)</option>
                  <option value="very_active">Very Active (Exercise 6-7 days/week)</option>
                  <option value="extra_active">Extra Active (Very intense exercise daily)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Used to calculate personalized nutrition targets</p>
              </div>

              {/* Goals */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Goal
                </label>
                <select
                  value={profile.primary_goal}
                  onChange={(e) => handleChange('primary_goal', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                >
                  <option value="">Select primary goal</option>
                  <option value="weight-loss">Weight Loss</option>
                  <option value="muscle-gain">Muscle Gain</option>
                  <option value="health-general">General Health</option>
                  <option value="medical">Medical/Therapeutic</option>
                  <option value="performance">Athletic Performance</option>
                  <option value="learning">Learning & Education</option>
                </select>
              </div>

              {/* Dietary Restrictions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dietary Restrictions
                </label>
                <textarea
                  value={profile.dietary_restrictions}
                  onChange={(e) => handleChange('dietary_restrictions', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                  placeholder="e.g., Vegetarian, Vegan, Gluten-free, etc."
                />
              </div>

              {/* Allergies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allergies
                </label>
                <textarea
                  value={profile.allergies}
                  onChange={(e) => handleChange('allergies', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                  placeholder="List any food allergies or intolerances"
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'preferences' && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">System Preferences</h3>
          </div>
          <div className="card-body">
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Dashboard Defaults</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Default analysis timeframe</span>
                    <select className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-900">
                      <option value="7d">7 days</option>
                      <option value="14d">14 days</option>
                      <option value="30d">30 days</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Auto-refresh data</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
